import { getStoreState } from 'config/store'
import { NEW_CONVERSATION_ID } from 'constants/conversations'
import { isEndUserMessage, toVisibleMessagesArray } from 'modules/message/utils'
import { find, isEmpty, isNil, path, pathOr, values } from 'ramda'
import { ConversationId } from 'types/conversation'
import { Message } from 'types/message'

/**
 * Gets the visible message list for the active conversation
 * This selector will filter out non-renderable messages, such as those with no body or are used to instead drop cards
 *
 * This is the selector to use when determining what messages belong in the renderable message list
 */
export const getVisibleActiveConversationMessages = (state): Message[] => {
  const { conversations = {} } = state
  const activeConversationId: ConversationId = conversations.activeConversation

  if (!activeConversationId) {
    return []
  }

  const conversationMessages = pathOr({}, ['messages', activeConversationId], conversations)
  if (isEmpty(conversationMessages)) {
    return []
  }

  return toVisibleMessagesArray(conversationMessages)
}

/**
 * Given a conversationId, grab the messages that exist for it in the redux store
 * @param conversationId - id of a given conversation
 */
export const getConversationMessages = (conversationId: ConversationId): Message[] => {
  const { conversations = {} } = getStoreState()
  const conversationMessages = pathOr({}, ['messages', conversationId], conversations)

  return values(conversationMessages)
}

/**
 * Given a conversationId, return true if there are end user messages in the conversation - false otherwise
 * @param conversationId '
 */
export const conversationHasEndUserMessages = (conversationId: ConversationId): boolean => {
  const conversationMessages = getConversationMessages(conversationId)

  if (!conversationMessages || !conversationMessages.length) {
    return false
  }

  const endUserMessage = find((message) => isEndUserMessage(message), conversationMessages)
  return !isNil(endUserMessage)
}

/**
 * Grabs the active conversation in state and determines if there have been any end user messages in that conversation
 */
export const hasActiveConversationBeenInteractedWith = () => {
  const state = getStoreState()
  const activeConversationId: ConversationId = getActiveConversationId(state)

  if (!activeConversationId) {
    return false
  }

  return conversationHasEndUserMessages(activeConversationId)
}

export const hasAnyConversations = (state): boolean => {
  const conversations = pathOr({},['conversations','conversations'], state)
  return !isEmpty(conversations)
}

export const getActiveConversationId = (state): ConversationId => path(['conversations', 'activeConversation'], state)

export const getActiveDriftlink = (state) => pathOr(null,['conversations','activeDriftlink'], state)

export const getCampaignConversations = (state) => pathOr([], ['conversations', 'campaignConversations'], state)

export const hasActiveConversation = (state): boolean => !!pathOr(null, ['conversations', 'activeConversation'], state)

export const getIsActiveConversationPermaclosed = (state): boolean => {
  const activeConversationId = getActiveConversationId(state)
  return pathOr(false, ['conversations', 'conversationStatus', activeConversationId, 'permaclosed'], state)
}

export const getIsActiveConversationNew = (state): boolean => {
  const activeConversationId = getActiveConversationId(state)
  return activeConversationId === NEW_CONVERSATION_ID
}

export const getIgnoredConversations = (state): ConversationId[] => {
  return pathOr([],['conversations', 'ignoredConversations'], state)
}
